import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { stringify } from 'qs'
import { useResurrectionUrlParams } from 'src/screens/resurrection/hooks/useResurrectionUrlParams'
import { useReactivation } from 'src/screens/resurrection/hooks/useReactivation'
import { useFeaturedMeals } from 'src/screens/resurrection/hooks/useFeaturedMeals'
import { setCookie, getCookie } from 'src/utils/browserCookies'
import { RESURRECTION_PATH_COOKIE_NAME } from 'src/constants/application'
import {
  trackResurrectionButtonClicked,
  trackResurrectionLandingPage
} from 'src/utils/tracking'

interface UseResurrectionControllerParams {
  location: {
    pathname: string
    search: string
  }
  subscriptionInfo?: {
    status: string
    currentCredit?: number
  } | null
  isReactivating: boolean
  authed: boolean
  actions: any
  resurrectionCampaign?: any
  isResurrectionCampaignLoading: boolean
  flagsReady: boolean
  coupon?: string
  prepaidOfferId?: string
}

export function useResurrectionController({
  location,
  subscriptionInfo,
  isReactivating,
  authed,
  actions,
  resurrectionCampaign,
  isResurrectionCampaignLoading,
  flagsReady,
  coupon,
  prepaidOfferId
}: UseResurrectionControllerParams) {
  const auth0 = useAuth0()
  const history = useHistory()
  const rawUrlParams = useResurrectionUrlParams(location)
  const urlParams = rawUrlParams ?? {
    chefBrandIds: [],
    mealIds: [],
    expiryDate: undefined,
    isExpired: false,
    isFromLogin: false,
    errorMessage: '',
    rawParams: {}
  }

  const [readyToRestart, setReadyToRestart] = useState<boolean>(
    urlParams.isFromLogin
  )
  const [openExpiredModal, setOpenExpiredModal] = useState<boolean>(
    urlParams.isExpired
  )

  // Fetch featured meals when campaign data is available
  useFeaturedMeals(resurrectionCampaign, urlParams.isFromLogin, actions)

  // Handle subscription reactivation - convert null to undefined for useReactivation
  useReactivation({
    readyToRestart,
    setReadyToRestart,
    subscriptionInfo: subscriptionInfo || undefined,
    isResurrectionCampaignLoading,
    flagsReady,
    resurrectionCampaign,
    urlParams: {
      chefBrandIds: urlParams.chefBrandIds,
      mealIds: urlParams.mealIds
    },
    actions,
    history,
    coupon,
    prepaidOfferId,
    pathname: location.pathname
  })

  // Track page load and set cookie
  useEffect(() => {
    trackResurrectionLandingPage(
      'Page Loaded',
      'Resurrection Landing Page Started'
    )

    if (!getCookie(RESURRECTION_PATH_COOKIE_NAME)) {
      setCookie(RESURRECTION_PATH_COOKIE_NAME, location.pathname, 1)
    }
  }, [location.pathname])

  const restartAction = (): void => {
    // Prevent re-trigger when already coming from Auth0
    if (urlParams.isFromLogin) return

    trackResurrectionButtonClicked(
      'Start Resurrection Flow',
      'Growth - Resurrection - Landing Page'
    )

    const { errorMessage, ...search } = urlParams.rawParams as Record<
      string,
      any
    >
    const searchString = stringify(search)

    // Offer expired – show modal and stop
    if (urlParams.isExpired) {
      toggleExpiredModal()
      return
    }

    // User already authenticated – call reactivation flow
    if (authed) {
      setReadyToRestart(true)
      return
    }

    // Otherwise redirect to Auth0 login and preserve params
    auth0.loginWithRedirect({
      action: 'login',
      appState: {
        returnTo: `${window.location.pathname}?fromLogin=true${
          searchString ? `&${searchString}` : ''
        }`
      },
      errorMessage
    })
  }

  const toggleExpiredModal = (): void => {
    setOpenExpiredModal(!openExpiredModal)
  }

  // Determine if we should show loading state
  const shouldShowLoader =
    auth0.isLoading ||
    (auth0.isAuthenticated && !subscriptionInfo) ||
    readyToRestart ||
    isReactivating ||
    isResurrectionCampaignLoading ||
    !flagsReady

  // Determine if user should be redirected
  const shouldRedirectToValidateCC =
    subscriptionInfo && subscriptionInfo.status.toLowerCase() === 'active'

  return {
    restartAction,
    toggleExpiredModal,
    openExpiredModal,
    shouldShowLoader,
    shouldRedirectToValidateCC,
    urlParams
  }
}
